import { ArrowUpRight, Hospital, UtensilsCrossed, Smartphone, Globe } from "lucide-react";

const projects = [
  {
    name: "Mercy Regional Patient Portal",
    category: "Hospital",
    icon: Hospital,
    accent: "#00f0ff",
    img: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    scope: ["Website", "Patient intake app"],
    result: "Online intake cut front-desk paperwork and gave the hospital a modern, trustworthy digital front door.",
  },
  {
    name: "Bistro 51 Online Ordering",
    category: "Restaurant",
    icon: UtensilsCrossed,
    accent: "#ff003c",
    img: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    scope: ["Ordering website", "Kitchen workflow app"],
    result: "Direct online ordering replaced phone chaos — orders flow straight to the kitchen screen.",
  },
  {
    name: "Sunrise Clinic Scheduler",
    category: "Hospital",
    icon: Smartphone,
    accent: "#00f0ff",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    scope: ["Scheduling app", "Automated reminders"],
    result: "Automated appointment scheduling and reminders reduced no-shows and freed up staff hours weekly.",
  },
  {
    name: "Ember & Oak Reservations",
    category: "Restaurant",
    icon: Globe,
    accent: "#ff003c",
    img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    scope: ["Brand website", "Reservation system"],
    result: "A high-end site with built-in reservations turned casual browsers into booked tables.",
  },
  {
    name: "CarePoint Internal Ops App",
    category: "Hospital",
    icon: Hospital,
    accent: "#00f0ff",
    img: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    scope: ["Staff workflow app", "Shift automation"],
    result: "Internal app automated shift coordination and supply requests across three departments.",
  },
  {
    name: "Taqueria Verde Loyalty App",
    category: "Restaurant",
    icon: Smartphone,
    accent: "#ff003c",
    img: "https://images.unsplash.com/photo-1552566626-52f8b828add9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    scope: ["Mobile app", "Loyalty & reorders"],
    result: "One-tap reordering and a loyalty program turned first-time guests into regulars.",
  },
];

export default function Portfolio() {
  return (
    <section id="work" className="circuit-bg relative border-t border-white/5 bg-[#050505] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00f0ff]">// Deployed systems</p>
            <h2 className="font-display mt-4 text-3xl font-black tracking-tight sm:text-4xl">
              Work that <span className="text-[#ff003c] text-glow-magenta">ships results</span>
            </h2>
            <p className="mt-4 text-base text-white/50 md:text-lg">
              A sample of the websites and apps we build for hospitals and restaurants.
            </p>
          </div>
          <p className="font-mono-tech max-w-xs text-[10px] uppercase tracking-widest text-white/30">
            Client identities adjusted — representative project showcase
          </p>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p, i) => (
            <article
              key={p.name}
              data-testid={`portfolio-card-${i + 1}`}
              className="group relative overflow-hidden border border-white/10 bg-[#0a0a0a] transition-colors duration-300 hover:border-white/30"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/45 transition-colors duration-300 group-hover:bg-black/25" />
                <span
                  className="font-mono-tech absolute left-4 top-4 flex items-center gap-1.5 border bg-black/70 px-2.5 py-1 text-[10px] uppercase tracking-widest backdrop-blur-sm"
                  style={{ borderColor: `${p.accent}66`, color: p.accent }}
                >
                  <p.icon size={11} /> {p.category}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display flex items-start justify-between gap-3 text-sm font-bold leading-snug">
                  {p.name}
                  <ArrowUpRight
                    size={16}
                    className="mt-0.5 shrink-0 text-white/25 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-[#00f0ff]"
                  />
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {p.scope.map((s) => (
                    <span
                      key={s}
                      className="font-mono-tech border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white/40"
                    >
                      {s}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/50">{p.result}</p>
              </div>
              <div
                className="absolute bottom-0 left-0 h-0.5 w-0 transition-[width] duration-500 group-hover:w-full"
                style={{ background: p.accent, boxShadow: `0 0 10px ${p.accent}` }}
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
